import React, { useState, useEffect } from 'react';
import emailjs from '@emailjs/browser';
import './contact.css';
const Contact = () => {
    const [form, setForm] = useState({ user_name: '', user_email: '', subject: '', message: '' });
    const [status, setStatus] = useState(null);

    // Read EmailJS configuration from environment (CRA requires REACT_APP_ prefix)
    const EMAILJS_PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;
    const EMAILJS_SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID;
    const EMAILJS_TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;

    // Initialize EmailJS lazily — calling init here avoids module-load errors when env is missing
    if (EMAILJS_PUBLIC_KEY) {
        try {
            emailjs.init(EMAILJS_PUBLIC_KEY);
        } catch (err) {
            console.warn('EmailJS initialization failed:', err);
        }
    } else {
        console.warn('REACT_APP_EMAILJS_PUBLIC_KEY is not set. EmailJS will require the public key to send messages.');
    }

    // Temporary debug: log whether env values were inlined into the production bundle.
    // After you confirm, we can remove these logs.
    useEffect(() => {
        try {
            const mask = v => (v ? (v.length > 6 ? v.slice(0, 4) + '...' + v.slice(-2) : v) : 'MISSING');
            console.info('EMAILJS CONFIG:', {
                publicKey: EMAILJS_PUBLIC_KEY ? 'SET' : 'MISSING',
                publicKeySample: EMAILJS_PUBLIC_KEY ? mask(EMAILJS_PUBLIC_KEY) : null,
                serviceId: EMAILJS_SERVICE_ID ? 'SET' : 'MISSING',
                templateId: EMAILJS_TEMPLATE_ID ? 'SET' : 'MISSING'
            });
        } catch (err) {
            console.warn('Unable to log EmailJS config:', err);
        }
    }, [EMAILJS_PUBLIC_KEY, EMAILJS_SERVICE_ID, EMAILJS_TEMPLATE_ID]);

    function handleChange(e) {
        setForm({ ...form, [e.target.name]: e.target.value });
    }

    function validate() {
        if (!form.user_name.trim() || !form.user_email.trim() || !form.subject.trim() || !form.message.trim()) return false;
        return /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.user_email);
    }

    async function handleSubmit(e) {
        e.preventDefault();
        if (!validate()) {
            setStatus({ type: 'error', message: 'Please fill all fields with a valid email.' });
            return;
        }

        setStatus({ type: 'sending', message: 'Sending...' });

        try {
            if (!EMAILJS_PUBLIC_KEY) {
                throw new Error('Missing EmailJS public key. Set REACT_APP_EMAILJS_PUBLIC_KEY in your environment.');
            }
            if (!EMAILJS_SERVICE_ID || !EMAILJS_TEMPLATE_ID) {
                throw new Error('Missing EmailJS service or template ID. Set REACT_APP_EMAILJS_SERVICE_ID and REACT_APP_EMAILJS_TEMPLATE_ID in your environment.');
            }

            // Pass public key explicitly as the fourth arg to avoid "public key is required" errors
            await emailjs.sendForm(EMAILJS_SERVICE_ID, EMAILJS_TEMPLATE_ID, e.target, EMAILJS_PUBLIC_KEY);

            setStatus({ type: 'success', message: '✅ Message sent successfully!' });
            setForm({ user_name: '', user_email: '', subject: '', message: '' });
        } catch (error) {
            console.error('EmailJS error:', error);
            const msg = error && error.message ? error.message : '❌ Failed to send message. Please try again.';
            setStatus({ type: 'error', message: msg });
        }
    }

    return (
        <section id="contact" className="contact-section-3d">
            <div className="contact-container-3d">
                <div className="contact-header-3d">
                    <h2 className="contact-title-3d">Contact Me</h2>
                    <p className="contact-subtitle-3d">Let's work together! Send me a message and I'll get back to you.</p>
                </div>

                <div className="contact-content-3d">
                    <div className="contact-form-container-3d">
                        <form id="contactForm" className="contact-form-3d" onSubmit={handleSubmit}>
                            <input type="hidden" name="date" value={new Date().toLocaleString()} />
                            <div className="form-group-3d">
                                <label htmlFor="user_name" className="form-label-3d">Name</label>
                                <input
                                    id="user_name"
                                    name="user_name"
                                    type="text"
                                    value={form.user_name}
                                    onChange={handleChange}
                                    placeholder="Your name"
                                    className="form-input-3d"
                                />
                            </div>

                            <div className="form-group-3d">
                                <label htmlFor="user_email" className="form-label-3d">Email</label>
                                <input
                                    id="user_email"
                                    name="user_email"
                                    type="email"
                                    value={form.user_email}
                                    onChange={handleChange}
                                    placeholder="you@example.com"
                                    className="form-input-3d"
                                />
                            </div>

                            <div className="form-group-3d">
                                <label htmlFor="subject" className="form-label-3d">Subject</label>
                                <input
                                    id="subject"
                                    name="subject"
                                    type="text"
                                    value={form.subject}
                                    onChange={handleChange}
                                    placeholder="Subject"
                                    className="form-input-3d"
                                />
                            </div>

                            <div className="form-group-3d">
                                <label htmlFor="message" className="form-label-3d">Message</label>
                                <textarea
                                    id="message"
                                    name="message"
                                    value={form.message}
                                    onChange={handleChange}
                                    placeholder="Write your message"
                                    rows={6}
                                    className="form-textarea-3d"
                                />
                            </div>

                            <button type="submit" className="contact-submit-btn-3d">
                                <span className="btn-text-3d">Send Message</span>
                                <span className="btn-icon-3d">📤</span>
                            </button>
                        </form>

                        {status && (
                            <div className={`contact-status-3d ${status.type}`} role="status">
                                <span className="status-icon-3d">
                                    {status.type === 'success' ? '✅' : status.type === 'error' ? '❌' : '⏳'}
                                </span>
                                <span className="status-text-3d">{status.message}</span>
                            </div>
                        )}
                    </div>

                    <div className="contact-info-3d">
                        <div className="contact-info-card-3d">
                            <div className="info-icon-3d">📧</div>
                            <h3 className="info-title-3d">Email</h3>
                            <p className="info-text-3d">Contact me through the form</p>
                        </div>

                        <div className="contact-info-card-3d">
                            <div className="info-icon-3d">📍</div>
                            <h3 className="info-title-3d">Location</h3>
                            <p className="info-text-3d">Available for remote work</p>
                        </div>

                        <div className="contact-info-card-3d">
                            <div className="info-icon-3d">⚡</div>
                            <h3 className="info-title-3d">Response Time</h3>
                            <p className="info-text-3d">Usually within 24 hours</p>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default Contact;